import axios from 'axios';
import Vue from 'vue';
import _pull from 'lodash/pull';
import _find from 'lodash/find';
import _union from 'lodash/union';
import _filter from 'lodash/filter';
import _find_index from 'lodash/findIndex';
import _pick_by from 'lodash/pickBy';
import { TREE, COLLECTION_DETAIL, COLLECTION_LIST } from '../api';
import { auth } from '../utils/requests';
import { request_mutations, request_state } from '../utils/store';
import { copy } from '../utils/helpers';
import { insert } from '../utils/data';

export default {
    namespaced: true,
    state: {
        open_notes: {},
        open_collections: [],
        tree: request_state(),
        update_collection: request_state(),
        create_collection: request_state(),
        remove_collection: request_state(),
        moving_element: { id: null, kind: null },
    },
    mutations: {
        ...request_mutations('tree', 'tree'),
        ...request_mutations('update_collection', 'update_collection'),
        ...request_mutations('create_collection', 'create_collection'),
        ...request_mutations('remove_collection', 'remove_collection'),
        duplicate_note(state, { note_id, new_id, name }) {
            const index = _find_index(state.tree.data, { id: note_id });
            if (index === -1) return;
            const note = copy(state.tree.data[index]);
            note.id = new_id;
            note.name = name;
            Vue.set(state.tree, 'data', insert(state.tree.data, index, note));
        },
        open_collection(state, { collection_ids }) {
            const collections = _union(state.open_collections, collection_ids);
            Vue.set(state, 'open_collections', collections);
        },
        close_collection(state, { collection_id }) {
            const collections = copy(_pull(state.open_collections, collection_id));
            Vue.set(state, 'open_collections', collections);
        },
        open_note(state, { topic_id, note_id }) {
            Vue.set(state.open_notes, topic_id, note_id);
        },
        remove_open_note(state, note_id) {
            const open_notes = _pick_by(state.open_notes, item => item !== note_id);
            Vue.set(state, 'open_notes', open_notes);
        },
        start_moving(state, data) {
            state.moving_element = data;
        },
        stop_moving(state) {
            state.moving_element = { id: null, kind: null };
        },
        clear_tree(state) {
            state.tree = request_state();
        },
        tree_expand(state, { parent_id, data }) {
            state.tree.loading = false;
            const parent = _find(state.tree.data, { id: parent_id, kind: 'collection' });
            Vue.set(parent, 'is_loaded', true);
            state.tree.data.push(...data);
        },
        change_parent(state, { id, parent_id }) {
            const item = _find(state.tree.data, { id });
            item.parent_collection = parent_id;
        },
        remove_item(state, { id, kind }) {
            const data = state.tree.data.filter(obj => obj.id !== id || obj.kind !== kind);
            Vue.set(state.tree, 'data', data);
        },
        remove_children(state, parent_id) {
            state.tree.data = _filter(state.tree.data, obj => obj.parent_collection !== parent_id);
        },
        add_item(state, {
            parent_id, data, kind, rename,
        }) {
            const state_data = state.tree.data;
            const parent = parent_id ? _find(state_data, { id: parent_id, kind: 'collection' }) : null;
            if (parent && !parent.is_loaded) return;
            Vue.set(data, 'kind', kind);
            Vue.set(data, 'parent_collection', parent_id);
            if (rename) Vue.set(data, 'is_renaming', true);
            state.tree.data = kind === 'note' ? [...state_data, data] : [data, ...state_data];
        },
        renaming_collection(state, { id, is_renaming }) {
            const collection = _find(state.tree.data, { id, kind: 'collection' });
            Vue.set(collection, 'is_renaming', is_renaming);
        },
        update_note_name(state, { note_id, name }) {
            const note = _find(state.tree.data, { id: note_id, kind: 'note' });
            if (note) note.name = name;
        },
    },
    actions: {
        list({ commit }, {
            space_id, topic_id, root = false, is_private = false, parents = [],
        }) {
            const url = TREE.replace('{space_id}', space_id);
            commit('tree_loading');
            axios.get(url, {
                params: {
                    topic: topic_id, private: is_private, parents, root,
                },
                ...auth(),
            }).then((response) => {
                parents.forEach((parent_id) => {
                    commit('remove_children', parent_id);
                    if (!root) {
                        commit('tree_expand', { parent_id, data: response.data });
                    }
                });

                if (root) {
                    commit('tree_success', response.data);
                }
            }).catch(error => commit('tree_error', error.response ? error.response.data : {}));
        },
        create_collection({ commit }, {
            space_id, parent_id, topic_id, name, is_private,
        }) {
            const url = COLLECTION_LIST.replace('{space_id}', space_id);
            commit('create_collection_loading');
            return new Promise((resolve) => {
                axios.post(url, {
                    name,
                    is_private,
                    parent: parent_id,
                    topic: topic_id,
                }, auth()).then((response) => {
                    commit('add_item', {
                        parent_id, data: response.data, kind: 'collection', rename: true,
                    });
                    commit('create_collection_success', response.data);
                    resolve();
                }).catch((error) => {
                    commit('create_collection_error', error.response ? error.response.data : {});
                });
            });
        },
        update_collection({ commit }, { space_id, collection_id, data }) {
            const url = COLLECTION_DETAIL
                .replace('{space_id}', space_id)
                .replace('{collection_id}', collection_id);

            commit('update_collection_loading');
            return new Promise((resolve) => {
                axios.patch(url, data, auth()).then((response) => {
                    commit('update_collection_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                    commit('update_collection_error', error.response ? error.response.data : {});
                });
            });
        },
        remove_collection({ commit }, { space_id, collection_id }) {
            const url = COLLECTION_DETAIL
                .replace('{space_id}', space_id)
                .replace('{collection_id}', collection_id);

            commit('remove_collection_loading');
            return new Promise((resolve) => {
                axios.delete(url, auth()).then((response) => {
                    commit('remove_collection_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                    commit('remove_collection_error', error.response ? error.response.data : {});
                });
            });
        },
        move_item({ commit, dispatch }, {
            space_id, id, move_to, kind,
        }) {
            if (kind === 'collection') {
                dispatch('update_collection', {
                    space_id,
                    collection_id: id,
                    data: { parent: move_to },
                }).then(() => commit('change_parent', { id, parent_id: move_to }));
            } else if (kind === 'note') {
                dispatch('notes/update', {
                    space_id,
                    note_id: id,
                    patch: true,
                    data: { collection: move_to },
                }, {
                    root: true,
                }).then(() => commit('change_parent', { id, parent_id: move_to }));
            }
        },
    },
    getters: {
        tree_structure: state => (parent) => {
            parent = parent || null;
            if (!Array.isArray(state.tree.data)) return [];
            return _filter(state.tree.data, { parent_collection: parent });
        },
    },
};
